import { useEffect, type ButtonHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type InputHTMLAttributes } from "react";

/**
 * Shared visual primitives for the client portal, built on the tokens and `rc-*` classes in
 * styles/tokens.css. Colour, hover, active and focus states live in that stylesheet (inline styles
 * can't express them); the components here only pick the right class and keep the same props they
 * have always had. A `style` prop is still merged last, so a screen can override anything.
 */

const cx = (...parts: Array<string | false | undefined>) => parts.filter(Boolean).join(" ");

export function Card({ children, style, padded = true, className }: { children: ReactNode; style?: React.CSSProperties; padded?: boolean; className?: string }) {
  return (
    <div className={cx("rc-card", className)} style={{ padding: padded ? 20 : 0, ...style }}>
      {children}
    </div>
  );
}

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export function Button({
  variant = "secondary",
  style,
  className,
  children,
  ...rest
}: { variant?: ButtonVariant } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...rest} className={cx("rc-btn", `rc-btn-${variant}`, className)} style={style}>
      {children}
    </button>
  );
}

type BadgeTone = "success" | "warning" | "danger" | "neutral" | "primary";

/** A status chip. The leading dot repeats the tone so it never relies on colour alone. */
export function Badge({ tone = "neutral", children }: { tone?: BadgeTone; children: ReactNode }) {
  const tones: Record<BadgeTone, { bg: string; text: string }> = {
    success: { bg: "var(--rc-success-soft)", text: "var(--rc-success)" },
    warning: { bg: "var(--rc-warning-soft)", text: "var(--rc-warning)" },
    danger: { bg: "var(--rc-danger-soft)", text: "var(--rc-danger)" },
    neutral: { bg: "var(--rc-neutral-soft)", text: "var(--rc-neutral)" },
    primary: { bg: "var(--rc-primary-soft)", text: "var(--rc-primary-soft-text)" },
  };
  const t = tones[tone];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 9px 2px 8px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.5,
        textTransform: "capitalize",
        background: t.bg,
        color: t.text,
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", flexShrink: 0 }} />
      {children}
    </span>
  );
}

export function Input({ style, className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cx("rc-field", className)} style={style} />;
}

export function Select({ style, className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={cx("rc-field", className)} style={style}>
      {children}
    </select>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" }}>
      {label}
      {children}
    </label>
  );
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="rc-segmented">
      {options.map((opt) => (
        <button key={opt.value} aria-pressed={opt.value === value} onClick={() => onChange(opt.value)}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function Modal({ title, onClose, children, width = 480 }: { title: ReactNode; onClose: () => void; children: ReactNode; width?: number }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--rc-overlay)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="rc-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--rc-surface)",
          border: "1px solid var(--rc-border)",
          borderRadius: "var(--rc-radius-lg)",
          boxShadow: "var(--rc-shadow-md)",
          color: "var(--rc-text)",
          width,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 64px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em", margin: 0, color: "var(--rc-text)" }}>{title}</h2>
          <button onClick={onClose} aria-label="Close" className="rc-modal-close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--rc-text-muted)", fontSize: 14 }}>{children}</div>;
}
